import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Radio,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconAlertCircle,
  IconDownload,
  IconTruckDelivery,
} from "@tabler/icons-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import { ReportSectionHeader } from "../ReportSectionHeader";
import { getContractsByAddressSystem } from "../../../services/ContractService";
import { generateToContractorReport } from "../../../services/ToContractorReportService";
import { ContractListItem } from "../../../types/UserRole";
import {
  ToContractorReportResponse,
} from "../../../types/ToContractorReport";

interface ToContractorReportProps {
  onBack: () => void;
  showHeader?: boolean;
}

const DATE_FORMAT = "DD.MM.YYYY";
const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format(DATE_FORMAT) : "";

export default function ToContractorReport({
  onBack,
  showHeader = true,
}: Readonly<ToContractorReportProps>) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const { getTranslation } = useTranslation({
    pageId: 517,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { checkAccess } = usePermissions();

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsError, setContractsError] = useState<string | null>(null);
  const [contractStatus, setContractStatus] = useState<"active" | "archived">(
    "active",
  );
  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [report, setReport] = useState<ToContractorReportResponse | null>(null);

  useEffect(() => {
    if (!selectedAddressSystemId) {
      setContracts([]);
      setSelectedContractId(null);
      return;
    }

    const loadContracts = async () => {
      setContractsLoading(true);
      setContractsError(null);
      try {
        const items = await getContractsByAddressSystem(
          selectedAddressSystemId,
        );
        setContracts(items);
      } catch (error) {
        console.error("Failed to load contracts", error);
        setContractsError(
          getTranslation("REPORTS_CONTRACTS_LOAD_ERROR") ||
            "Unable to load contracts for the address system.",
        );
      } finally {
        setContractsLoading(false);
      }
    };

    loadContracts();
  }, [selectedAddressSystemId]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) =>
      contractStatus === "active"
        ? contract.contractStatusDefaultValue === 1
        : contract.contractStatusDefaultValue !== 1,
    );
  }, [contracts, contractStatus]);

  useEffect(() => {
    if (filteredContracts.length > 0) {
      setSelectedContractId((current) => {
        const stillVisible = filteredContracts.some(
          (c) => String(c.id) === current,
        );
        return stillVisible ? current : String(filteredContracts[0].id);
      });
    } else {
      setSelectedContractId(null);
    }
  }, [filteredContracts]);

  const selectedContract = useMemo(
    () =>
      filteredContracts.find((c) => String(c.id) === selectedContractId) ??
      null,
    [filteredContracts, selectedContractId],
  );

  const handleGenerate = async () => {
    if (!selectedContractId) {
      setGenerationError(
        getTranslation("REPORTS_INVOICED_NO_CONTRACT") ||
          "Select a contract.",
      );
      return;
    }

    setGenerationError(null);
    setGenerating(true);
    try {
      const result = await generateToContractorReport({
        addressSystemId: selectedAddressSystemId,
        currentUserId,
        contractId: Number(selectedContractId),
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
      });
      setReport(result);
    } catch (error) {
      console.error("Failed to generate contractor report", error);
      setGenerationError(
        getTranslation("REPORTS_GENERATE_ERROR") ||
          "Failed to generate the report. Please try again.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const columnLabels = () => ({
    location: getTranslation("REPORTS_COLUMN_LOCATION") || "Location",
    foreman: getTranslation("REPORTS_COLUMN_FOREMAN") || "Foreman",
    type: getTranslation("REPORTS_COLUMN_TYPE") || "Type",
    plannedDate: getTranslation("REPORTS_COLUMN_PLANNED_DATE") || "Planned date",
    readyDate: getTranslation("REPORTS_COLUMN_READY_DATE") || "Ready date",
    planned: getTranslation("REPORTS_COLUMN_PLANNED_PRICE") || "Planned price",
    actual: getTranslation("REPORTS_COLUMN_ACTUAL_PRICE") || "Actual price",
    difference: getTranslation("REPORTS_COLUMN_DIFFERENCE") || "Difference",
  });

  const handleDownloadXlsx = () => {
    if (!report || !selectedContract) return;
    const labels = columnLabels();
    const sheetData: (string | number)[][] = [
      [
        getTranslation("REPORTS_TO_CONTRACTOR_SHEET_TITLE") ||
          "Given to contractor report",
      ],
      [
        getTranslation("REPORTS_SHEET_CONTRACT") || "Contract",
        report.contract.name,
      ],
      [getTranslation("REPORTS_SHEET_CREATED") || "Created", dayjs().format(DATE_FORMAT)],
      [],
      [
        labels.location,
        labels.foreman,
        labels.type,
        labels.plannedDate,
        labels.readyDate,
        labels.planned,
        labels.actual,
        labels.difference,
      ],
      ...report.rows.map((row) => [
        row.location || "",
        row.foremanName || "",
        row.typeLabel || "",
        formatDate(row.plannedDate),
        formatDate(row.readyDate),
        row.plannedPrice,
        row.actualPrice,
        row.difference,
      ]),
      [],
      [
        getTranslation("REPORTS_TOTAL") || "Total",
        "",
        "",
        "",
        "",
        report.totalPlannedPrice,
        report.totalActualPrice,
        report.totalDifference,
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 24 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      getTranslation("REPORTS_TO_CONTRACTOR_SHEET_TAB") || "Contractor",
    );
    const array = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([array], { type: EXCEL_MIME });
    saveAs(
      blob,
      `contractor-report-${dayjs().format("YYYYMMDD")}.xlsx`,
    );
  };

  if (!checkAccess("CITY_OBJECT_TO_CONTRACTOR_REPORT")) {
    return (
      <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
        <Alert color="yellow">
          {getTranslation("REPORTS_NO_ACCESS") ||
            "You do not have permission to view this report."}
        </Alert>
      </Card>
    );
  }

  const labels = columnLabels();

  const contractSelector = (() => {
    if (contractsLoading) {
      return (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {getTranslation("REPORTS_LOADING_CONTRACTS") ||
              "Loading contracts..."}
          </Text>
        </Group>
      );
    }

    if (filteredContracts.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          {getTranslation("REPORTS_NO_CONTRACTS_FOR_STATUS") ||
            "No contracts available for the selected status."}
        </Text>
      );
    }

    return (
      <Radio.Group
        value={selectedContractId}
        onChange={setSelectedContractId}
        styles={{ label: { fontWeight: 500 } }}
      >
        <ScrollArea.Autosize mah={220}>
          <Stack gap={4}>
            {filteredContracts.map((contract) => (
              <Radio
                key={contract.id}
                value={String(contract.id)}
                label={contract.name}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </Radio.Group>
    );
  })();

  return (
    <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
      <Stack gap="md">
        {showHeader && (
          <ReportSectionHeader
            onBack={onBack}
            contextLabel={getTranslation("REPORTS_HUB_LABEL") || "Reports"}
            title={
              getTranslation("REPORTS_TO_CONTRACTOR_TITLE") ||
              "Search for given to contractor objects"
            }
            backLabel={getTranslation("REPORTS_RETURN") || "Back to reports"}
            leadingVisual={
              <ThemeIcon size="xl" radius="xl" color="blue" variant="light">
                <IconTruckDelivery size={24} />
              </ThemeIcon>
            }
          />
        )}

        {showHeader && <Divider variant="dashed" />}

        {contractsError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {contractsError}
          </Alert>
        )}

        <Group justify="space-between" align="flex-end">
          <Text fw={600}>
            {getTranslation("REPORTS_CONTRACT_LABEL") || "Contract:"}
          </Text>
          <Select
            size="xs"
            w={170}
            value={contractStatus}
            onChange={(value) =>
              setContractStatus((value as "active" | "archived") ?? "active")
            }
            data={[
              {
                value: "active",
                label:
                  getTranslation("REPORTS_ACTIVE_CONTRACTS") ||
                  "Active Contracts",
              },
              {
                value: "archived",
                label:
                  getTranslation("REPORTS_ARCHIVED_CONTRACTS") ||
                  "Archived Contracts",
              },
            ]}
          />
        </Group>
        {contractSelector}

        <Group grow>
          <DateInput
            label={
              getTranslation("REPORTS_PREVIOUS_MEETING_DATE") ||
              "Date of the previous meeting:"
            }
            valueFormat={DATE_FORMAT}
            placeholder={DATE_FORMAT}
            clearable
            value={startDate}
            onChange={setStartDate}
          />
          <DateInput
            label={
              getTranslation("REPORTS_CURRENT_MEETING_DATE") ||
              "Date of this meeting:"
            }
            valueFormat={DATE_FORMAT}
            placeholder={DATE_FORMAT}
            clearable
            value={endDate}
            onChange={setEndDate}
          />
        </Group>

        {generationError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {generationError}
          </Alert>
        )}

        <Group>
          <Button
            onClick={handleGenerate}
            loading={generating}
            disabled={!selectedContractId}
          >
            {getTranslation("REPORTS_CREATE_BUTTON") || "Create a report"}
          </Button>
        </Group>

        {report && (
          <Stack gap="sm">
            <Divider />
            <Group justify="space-between">
              <Text fw={600}>
                {(getTranslation("REPORTS_RESULTS_FOUND") ||
                  "Search found {count} objects").replace(
                  "{count}",
                  String(report.rows.length),
                )}
              </Text>
              {report.rows.length > 0 && (
                <Anchor
                  component="button"
                  type="button"
                  onClick={handleDownloadXlsx}
                >
                  <Group gap={4}>
                    <IconDownload size={16} />
                    {getTranslation("REPORTS_DOWNLOAD_XLSX") ||
                      "Download as XLSX"}
                  </Group>
                </Anchor>
              )}
            </Group>

            {report.rows.length > 0 ? (
              <ScrollArea>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{labels.location}</Table.Th>
                      <Table.Th>{labels.foreman}</Table.Th>
                      <Table.Th>{labels.type}</Table.Th>
                      <Table.Th>{labels.plannedDate}</Table.Th>
                      <Table.Th>{labels.readyDate}</Table.Th>
                      <Table.Th ta="right">{labels.planned}</Table.Th>
                      <Table.Th ta="right">{labels.actual}</Table.Th>
                      <Table.Th ta="right">{labels.difference}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {report.rows.map((row) => (
                      <Table.Tr key={row.id}>
                        <Table.Td>{row.location}</Table.Td>
                        <Table.Td>{row.foremanName}</Table.Td>
                        <Table.Td>{row.typeLabel}</Table.Td>
                        <Table.Td>{formatDate(row.plannedDate)}</Table.Td>
                        <Table.Td>{formatDate(row.readyDate)}</Table.Td>
                        <Table.Td ta="right">
                          {formatCurrency(row.plannedPrice)}
                        </Table.Td>
                        <Table.Td ta="right">
                          {formatCurrency(row.actualPrice)}
                        </Table.Td>
                        <Table.Td ta="right">
                          {formatCurrency(row.difference)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                  <Table.Tfoot>
                    <Table.Tr>
                      <Table.Th colSpan={5} ta="right">
                        {getTranslation("REPORTS_TOTAL") || "Total"}
                      </Table.Th>
                      <Table.Th ta="right">
                        {formatCurrency(report.totalPlannedPrice)}
                      </Table.Th>
                      <Table.Th ta="right">
                        {formatCurrency(report.totalActualPrice)}
                      </Table.Th>
                      <Table.Th ta="right">
                        {formatCurrency(report.totalDifference)}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
              </ScrollArea>
            ) : (
              <Text size="sm" c="dimmed">
                {getTranslation("REPORTS_NO_RESULTS") ||
                  "The search returned no results."}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
